import type { ConnectionData, ConnectionType } from '~/services/serviceInterface';
import * as Sentry from '@sentry/remix';

export const handleCodeImport = (
  e: React.ChangeEvent<HTMLInputElement>,
  codeFiles: Array<{ name: string; content: string }>,
  onCodeFilesUpdate: (files: Array<{ name: string; content: string }>) => void,
) => {
  const items = e.target.files;

  if (items) {
    const newFiles: Array<{ name: string; content: string }> = [];

    const readFile = (file: File) => {
      const reader = new FileReader();

      reader.onload = (e) => {
        const content = e.target?.result as string;
        newFiles.push({ name: file.name, content });

        if (newFiles.length === items.length) {
          // create a map of existing files for quick lookup
          const existingFilesMap = new Map(codeFiles.map((file) => [file.name, file]));

          // process new files
          const updatedFiles = newFiles.reduce(
            (acc, newFile) => {
              // if file with same name exists, update its content
              if (existingFilesMap.has(newFile.name)) {
                return acc.map((file) => (file.name === newFile.name ? { ...file, content: newFile.content } : file));
              }

              // if file doesn't exist, add it to the array
              return [...acc, newFile];
            },
            [...codeFiles],
          );

          onCodeFilesUpdate(updatedFiles);
        }
      };
      reader.readAsText(file);
    };

    Array.from(items).forEach((file) => {
      readFile(file);
    });
  }
};

export const handleThemeImport = (
  e: React.ChangeEvent<HTMLInputElement>,
  onThemeFileUpdate: (file: { name: string; content: string }) => void,
) => {
  const file = e.target.files?.[0];

  if (file) {
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result as string;
      const themeFile = { name: file.name, content };
      onThemeFileUpdate(themeFile);
    };
    reader.readAsText(file);
  }
};

export const updateProjectConnection = async (projectId: string, connectionData: ConnectionData) => {
  try {
    const response = await fetch('/api/connections', {
      method: 'POST',
      body: JSON.stringify({ projectId, connectionData }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error updating ${connectionData.connectionType} connection`);
    }
  } catch (error) {
    console.error('[updateProjectConnection] Error updating project connection:', error);
    Sentry.captureException(error, {
      extra: {
        message: 'Error updating project connection',
        projectId,
        connectionData,
      },
    });
  }
};

export const deleteProjectConnection = async (projectId: string, connectionType: ConnectionType) => {
  try {
    const response = await fetch('/api/connections', {
      method: 'DELETE',
      body: JSON.stringify({ projectId, connectionType }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error deleting ${connectionType} connection`);
    }
  } catch (error: any) {
    console.error('[deleteProjectConnection] Error deleting project connection:', error);

    Sentry.captureException(error, {
      extra: {
        message: 'Error deleting project connection',
        projectId,
        connectionType,
      },
    });
    throw new Error(error);
  }
};

export const updateIsGitStatus = async (projectId: string, isGit: boolean) => {
  try {
    const response = await fetch('/api/projects', {
      method: 'PUT',
      body: JSON.stringify({ projectId, isGit }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const responseData = (await response.json()) as {
      ok: boolean;
      message: string;
      error: string;
    };

    if (!responseData.ok) {
      throw new Error(responseData.error);
    }
  } catch (error) {
    console.error('[updateIsGitStatus] Error updating is git status:', error);
    Sentry.captureException(error, {
      extra: {
        message: 'Error updating is git status',
        projectId,
        isGit,
      },
    });
    throw new Error('Error updating is git status');
  }
};
